import { createContext, useCallback, useEffect, useState } from "react";
import { Table } from "react-bootstrap"
import { CService } from "services/class.base.service";
import { childrenCompare } from "utils";
import Export from "./export_excel";
import { IColumnNewTable } from "./interfaces"
import TableBody from "./TableBody";
import TableHead from "./TableHead";
import TablePagination from "./TablePagination";
import TableTopBar from "./TableTopBar";
import { verifyListObj } from "./utils";
import "./style/_newtable.scss";
import { collect } from "utils/classes/collection";

class Aux{
  static order = 1;
  static getOrder(){
    return this.order++;
  }
}

export interface IFilter{
  [x:string]: any;
  __search?: string;
  page: number;
  per_page: number;
}

export interface IExpandRow{
  render: (item:any, row:number) => JSX.Element | any[];
  type?:"COL"|"DEFAULT"|"ROW";
  getColumnInfo?:boolean;
  style?:any;
}
interface INewTableProps{
  columns?: IColumnNewTable[];
  data?: any[];
  children?: JSX.Element[] |  JSX.Element | any;
  pagination?: any ;
  filters?: IFilter;
  load?: boolean;
  export?: boolean;
  exportSelectColumns?: boolean;
  nameTable?: string;
  id?: string;
  expandRow?: IExpandRow;
  hideTopBar?: boolean;
  tableLayoutFixed?: boolean;
}

/**
 * 
 * @param param INewTableProps
 * @returns 
 */
export default function NewTable({
  columns = [], 
  data, 
  pagination = {}, 
  load = true,
  nameTable = "New Table",
  filters = {__search:"", page:1, per_page:10},
  id,
  expandRow,
  children,
  hideTopBar,
  tableLayoutFixed,
  ...props
}:INewTableProps){
  const [ columnsInternal, setColumnsInternal ] = useState<IColumnNewTable[]>(columns);
  const [ dataInternal, setDataInternal ] = useState<any[]>([]);
  const [ paginationInternal, setPaginationInternal ] = useState(pagination);
  const [ filtersInternal, setFiltersInternal ] = useState<IFilter>(filters);
  const [ loadInternal, setLoadInternal ] = useState<boolean>(load);
  const [ requestInternal, setRequestInternal ] = useState<CService | undefined>();
  const [ internalChildren, setInternalChildren ] = useState<any[]>(()=>{
    let list = [];
    function getList(item:any|any[]){
      if(Array.isArray(item)){
        for (const iterator of item) {
          getList(iterator);
        }
      } else {
        list.push(item);
      }
    }
    if(Array.isArray(children)){
      getList(children);
    } else {
      list.push(children);
    }
    return list;
  });

  const addColumns = useCallback((newColumn: IColumnNewTable)=>{
    setColumnsInternal((old: IColumnNewTable[])=>{
      if(!verifyListObj(old, "id",newColumn.id)){
        return collect([...old, newColumn]).sortBy("order").toArray();
      }
      return collect([...old]).sortBy("order").toArray();
    });
  },[]);

  const removeColumn = useCallback((id: string) =>{
    setColumnsInternal((old: IColumnNewTable[])=>{
      // let index = old.indexOf(newColumn);
      // if(index !== -1){
      //   let aux = old.splice(index,1);
      //   return [...aux];
      // }
      return [...old.filter((item)=>item.id !== id)];
    });
  },[]);

  const setData = useCallback((data, pagination, load = false)=>{
    setDataInternal(data);
    setPaginationInternal(pagination);
    setLoadInternal(load);
  },[]);

  const setFilter = useCallback((filters)=>{
    setFiltersInternal(old=>({...old, ...filters}));
  },[]);

  const setRequest = useCallback((request:CService)=>{
    setRequestInternal(request);
  },[]);

  const exportData = useCallback(async (columnsSelected = [])=>{
    if(requestInternal){
      let columns = columnsInternal.filter((item, i) => {
        if(columnsSelected[i]){
          return !item.isAction;
        }
        return false;
      });

      let ex = new Export(nameTable, filtersInternal);
      ex.request(async (params: any) => {return await requestInternal.getPaginated(params);});
      let list = await ex.run();
      ex.exportTable(
        <Table bordered responsive className="w-100 new-table">
          <TableHead columns={columns}/>
          <TableBody data={list} columns={columns}/>
        </Table>
      );
    }
  },[nameTable, filtersInternal, requestInternal, columnsInternal]);

  const getOrder = useCallback(()=>{
    return Aux.getOrder();
  },[]);

  useEffect(()=>{
    if(data)setData(data,{});
  },[data, setData]);

  useEffect(()=>{
    let list = [];
    function getList(item:any|any[]){
      if(Array.isArray(item)){
        for (const iterator of item) {
          getList(iterator);
        }
      } else {
        list.push(item);
      }
    }
    if(Array.isArray(children)){
      getList(children);
    } else {
      list.push(children);
    }
    if(!childrenCompare(internalChildren,list)){
      setInternalChildren(list);
    }
    
  },[children, internalChildren]);

  useEffect(()=>{
    setLoadInternal(load);
  },[load])

  function renderChildren(children: any, f_string: string, info:null|string = null){
    let __return = [];
    try {
      if(children[0]){
        for (let i = 0; i < children.length; i++) {
          const element = children[i];
          
          if(element && element.type && element.type && element.type.toString() === f_string){
            __return.push(element);
          } else if(element && element.type && element.type.type && element.type.type.toString() === f_string){
            __return.push(element);
          }
        }
      } else {
        if(children && children.type.toString() === f_string){
          __return.push(children);
        }
      }
      return __return;
    } catch (error) {
      return <></>;
    }
  };

  return (
    <NewTableContext.Provider value={{
      addColumns, 
      removeColumn,
      setData, 
      filters: filtersInternal, 
      setLoad: setLoadInternal,
      setFilter,
      setRequest,
      exportData,
      getOrder
    }}>
      <div className="new-table-area" id={id}>
        {renderChildren(internalChildren, require("./TableNav").default.toString(), "TableNav")}
        {renderChildren(internalChildren, require("./TableFilter").default.toString(), "TableFilter")}
        {hideTopBar?<></>:(
          <TableTopBar export={props.export} exportSelectColumns={props.exportSelectColumns} columns={columnsInternal.filter((item) => !item.isAction)} onChange={(e:any)=>{
            if(e.per_page.change) setFiltersInternal(old => ({...old, per_page: e.per_page.value}))
            if(e.__search.change) setFiltersInternal(old => ({...old, __search: e.__search.value}))
          }}/>
        )}
        <Table responsive className="w-100 new-table" style={{tableLayout:(tableLayoutFixed?"fixed":"auto")}}>
          <TableHead columns={columnsInternal} hasExpandRow={!!expandRow}>
          {renderChildren(internalChildren, require("./TableHead").TableHeaderColumn.toString(), "TableHead")}
          </TableHead>
          <TableBody data={dataInternal} columns={columnsInternal} load={loadInternal} expandRow={expandRow}>
          {renderChildren(internalChildren, require("./TableBody").TableBodyData.type.toString(), "TableBody")}
          </TableBody>
        </Table>
        <TablePagination
          activePage={paginationInternal.current_page}
          itemsCountPerPage={paginationInternal.per_page}
          totalItemsCount={paginationInternal.total}
          onChange={(value: number)=>{
            setFiltersInternal(old=>({...old, page:value}))
          }}
          from={paginationInternal.from}
          to={paginationInternal.to}
        /> 
      </div>
    </NewTableContext.Provider>
  );
}

interface IValueNewTableContext {
  addColumns?: Function;
  removeColumn?: Function;
  setData?: Function;
  filters?: IFilter;
  setLoad?: Function;
  setFilter?: Function;
  setRequest?: Function;
  exportData?: Function;
  getOrder?: Function;
}
export const NewTableContext = createContext<IValueNewTableContext>({});
export { TableHeaderColumn } from "./TableHead";
export { TableBodyData } from "./TableBody";
export { default as TableFilter } from "./TableFilter";
export { default as TableNav } from "./TableNav";